import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, query,
  orderBy, deleteDoc, doc
} from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';

export default function AllReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [readMode, setReadMode] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  async function fetchReports() {
    try {
      const q = query(
        collection(db, 'reports'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setReports(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (window.confirm(
      'Are you sure you want to delete this report?'
    )) {
      await deleteDoc(doc(db, 'reports', id));
      fetchReports();
      setSelected(null);
    }
  }

  const filtered = reports.filter(r => {
    const matchSearch = `${r.title} ${r.reportType} 
      ${r.personId} ${r.reportId}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchFilter =
      filter === 'all' ? true :
      filter === 'student' ? r.relatedTo === 'student' :
      filter === 'parent' ? r.relatedTo === 'parent' :
      filter === 'session' ? r.relatedTo === 'session' :
      filter === 'followup' ? r.followUpNeeded === true :
      true;
    return matchSearch && matchFilter;
  });

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>All Reports</div>
            <div style={styles.pageSub}>
              View all staff submitted reports
            </div>
          </div>
        </div>

        {/* STATS */}
        <div style={styles.statsRow}>
          {[
            { label: 'Total Reports', value: reports.length, color: '#14452F' },
            { label: 'Student Reports', value: reports.filter(r => r.relatedTo === 'student').length, color: '#F57C00' },
            { label: 'Parent Reports', value: reports.filter(r => r.relatedTo === 'parent').length, color: '#4527A0' },
            { label: 'Follow Ups', value: reports.filter(r => r.followUpNeeded).length, color: '#C62828' },
          ].map((s, i) => (
            <div key={i} style={styles.statPill}>
              <div style={{ ...styles.statNum, color: s.color }}>
                {s.value}
              </div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* SEARCH */}
        <div style={styles.searchBar}>
          <span></span>
          <input
            style={styles.searchInput}
            placeholder="Search by title, type or ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* FILTERS */}
        <div style={styles.filterRow}>
          {[
            { id: 'all', label: 'All' },
            { id: 'student', label: 'Student' },
            { id: 'parent', label: 'Parent' },
            { id: 'session', label: 'Session' },
            { id: 'followup', label: 'Follow Up' },
          ].map(f => (
            <button
              key={f.id}
              style={{
                ...styles.filterBtn,
                ...(filter === f.id ? styles.filterActive : {})
              }}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={styles.grid}>

          {/* REPORTS LIST */}
          <div style={styles.leftPanel}>
            {loading ? (
              <div style={styles.empty}>Loading reports...</div>
            ) : filtered.length === 0 ? (
              <div style={styles.empty}>No reports found.</div>
            ) : (
              filtered.map(r => (
                <div
                  key={r.id}
                  style={{
                    ...styles.reportItem,
                    ...(selected?.id === r.id
                      ? styles.reportItemActive : {})
                  }}
                  onClick={() => setSelected(r)}
                >
                  <div style={styles.reportIcon}>
                    {r.relatedTo === 'student' ? '' :
                     r.relatedTo === 'parent' ? '' :
                     r.relatedTo === 'session' ? '' : ''}
                  </div>
                  <div style={styles.reportInfo}>
                    <div style={styles.reportTitle}>
                      {r.title}
                    </div>
                    <div style={styles.reportMeta}>
                      {r.reportType} ·{' '}
                      {r.createdAt?.toDate?.()
                        .toLocaleDateString() || 'Recently'}
                    </div>
                  </div>
                  {r.followUpNeeded && (
                    <span style={styles.followUpDot}></span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* REPORT DETAIL */}
          <div style={styles.rightPanel}>
            {!selected ? (
              <div style={styles.noSelection}>
                <div style={styles.noSelectionIcon}></div>
                <div style={styles.noSelectionText}>
                  Select a report to view details
                </div>
              </div>
            ) : (
              <div style={styles.detailCard}>

                {/* HEADER */}
                <div style={styles.detailHeader}>
                  <div>
                    <div style={styles.detailTitle}>
                      {selected.title}
                    </div>
                    <div style={styles.detailMeta}>
                      {selected.reportId} ·
                      {selected.reportType} ·
                      {selected.createdAt?.toDate?.()
                        .toLocaleDateString() || 'Recently'}
                    </div>
                  </div>
                  <div style={styles.detailHeaderBtns}>
                    <button
                      style={styles.readBtn}
                      onClick={() => setReadMode(true)}
                    >
                      Soma Kamili
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(selected.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* BADGES */}
                <div style={styles.badgeRow}>
                  <span style={styles.badge}>
                    {selected.relatedTo === 'student' ? 'Student Report' :
                     selected.relatedTo === 'parent' ? 'Parent Report' :
                     selected.relatedTo === 'session' ? 'Session Report' :
                     'General Report'}
                  </span>
                  <span style={{
                    ...styles.badge,
                    background: '#EAF3EC',
                    color: '#14452F'
                  }}>
                    {selected.status || 'Submitted'}
                  </span>
                  {selected.followUpNeeded && (
                    <span style={{
                      ...styles.badge,
                      background: '#FFEBEE',
                      color: '#C62828'
                    }}>
                      Follow Up: {selected.followUpDate}
                    </span>
                  )}
                </div>

                {/* DESCRIPTION */}
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>
                    Description
                  </div>
                  <div style={styles.sectionText}>
                    {selected.description}
                  </div>
                </div>

                {/* PROGRESS */}
                {selected.academicProgress && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      Progress Assessment
                    </div>
                    {[
                      { label: 'Academic', value: selected.academicProgress },
                      { label: 'Behaviour (Tabia)', value: selected.behaviourProgress },
                      { label: 'Home (Nyumbani)', value: selected.homeProgress },
                      { label: 'Health', value: selected.healthProgress },
                      { label: '🏘️ Community', value: selected.communityProgress },
                      { label: 'Life Skills', value: selected.lifeSkillsProgress },
                    ].map((p, i) => p.value && (
                      <div key={i} style={styles.progressRow}>
                        <span style={styles.progressLabel}>
                          {p.label}
                        </span>
                        <span style={{
                          ...styles.progressBadge,
                          background:
                            p.value === 'Excellent' ? '#EAF3EC' :
                            p.value === 'Good' ? '#EAF3EC' :
                            p.value === 'Average' ? '#FFF3E0' :
                            '#FFEBEE',
                          color:
                            p.value === 'Excellent' ? '#14452F' :
                            p.value === 'Good' ? '#1D5C3C' :
                            p.value === 'Average' ? '#E65100' :
                            '#C62828',
                        }}>
                          {p.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* RECOMMENDATIONS */}
                {selected.recommendations && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      Recommendations
                    </div>
                    <div style={styles.sectionText}>
                      {selected.recommendations}
                    </div>
                  </div>
                )}

                {/* PHOTOS */}
                {selected.photoURLs?.length > 0 && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      Photos ({selected.photoURLs.length})
                    </div>
                    <div style={styles.photoGrid}>
                      {selected.photoURLs.map((p, i) => (
                        <img
                          key={i}
                          src={p.url}
                          alt={`photo-${i}`}
                          style={styles.photo}
                          onClick={() => window.open(p.url)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* DOCUMENTS */}
                {selected.documentURLs?.length > 0 && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      Documents ({selected.documentURLs.length})
                    </div>
                    {selected.documentURLs.map((d, i) => (
                      <div
                        key={i}
                        style={styles.docItem}
                        onClick={() => window.open(d.url)}
                      >
                        <span></span>
                        <span style={styles.docName}>
                          {d.name}
                        </span>
                        <span style={styles.docOpen}>
                          Open →
                        </span>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>

      {/* FULL-SCREEN READING MODE — comfortable typography for long reports */}
      {readMode && selected && (
        <div style={styles.readOverlay} onClick={() => setReadMode(false)}>
          <div style={styles.readPage} onClick={e => e.stopPropagation()}>
            <div style={styles.readHeader}>
              <div>
                <div style={styles.readTitle}>{selected.title}</div>
                <div style={styles.readMeta}>
                  {selected.personName ? `${selected.personName} · ` : ''}
                  {selected.reportType} · {selected.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) || 'Recently'}
                </div>
              </div>
              <button style={styles.readCloseBtn} onClick={() => setReadMode(false)}>✕ Funga</button>
            </div>
            <div style={styles.readBody}>
              {(selected.description || '').split('\n').filter(p => p.trim()).map((para, i) => (
                <p key={i} style={styles.readPara}>{para}</p>
              ))}
              {selected.recommendations && (
                <>
                  <div style={styles.readSectionTitle}>Recommendations</div>
                  {(selected.recommendations || '').split('\n').filter(p => p.trim()).map((para, i) => (
                    <p key={i} style={styles.readPara}>{para}</p>
                  ))}
                </>
              )}
              {selected.followUpNeeded && (
                <div style={styles.readFollowUp}>
                  Follow up inahitajika: {selected.followUpDate}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  detailHeaderBtns: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 },
  readBtn: { background: '#14452F', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  readOverlay: { position: 'fixed', inset: 0, background: 'rgba(10,20,14,0.75)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '30px 16px' },
  readPage: { background: '#FDFDFB', borderRadius: '18px', maxWidth: '780px', width: '100%', boxShadow: '0 24px 70px rgba(0,0,0,0.4)', overflow: 'hidden' },
  readHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '26px 34px 18px', borderBottom: '1px solid #EDEBE4' },
  readTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '24px', fontWeight: '700', color: '#1C1C1E', lineHeight: 1.3 },
  readMeta: { fontSize: '12.5px', color: '#6B7280', marginTop: '6px' },
  readCloseBtn: { background: 'transparent', border: '1.5px solid #D1D5DB', color: '#4B5563', padding: '8px 16px', borderRadius: '9px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  readBody: { padding: '26px 34px 40px', maxWidth: '68ch' },
  readPara: { fontSize: '15.5px', lineHeight: 1.9, color: '#2C2C2E', marginBottom: '16px' },
  readSectionTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '17px', fontWeight: '700', color: '#14452F', margin: '26px 0 12px' },
  readFollowUp: { background: '#FFF3E0', border: '1px solid #FFE0B2', color: '#9A5B00', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', marginTop: '20px' },
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  statsRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  statPill: {
    background: '#fff',
    borderRadius: '12px',
    padding: '12px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    textAlign: 'center',
    minWidth: '80px',
  },
  statNum: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '22px',
    fontWeight: '700',
  },
  statLabel: {
    fontSize: '11px',
    color: '#6B7280',
    marginTop: '2px',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#fff',
    borderRadius: '12px',
    padding: '10px 16px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13.5px',
    fontFamily: 'inherit',
    background: 'transparent',
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterBtn: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1.5px solid #E5E7EB',
    background: 'transparent',
    color: '#6B7280',
    fontSize: '12.5px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filterActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent',
    color: '#fff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '20px',
  },
  leftPanel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  reportItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '4px',
    transition: 'all 0.15s',
    border: '1px solid transparent',
  },
  reportItemActive: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
  },
  reportIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    flexShrink: 0,
  },
  reportInfo: { flex: 1 },
  reportTitle: {
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '2px',
  },
  reportMeta: {
    fontSize: '11px',
    color: '#6B7280',
  },
  followUpDot: { fontSize: '14px' },
  rightPanel: { display: 'flex', flexDirection: 'column' },
  noSelection: {
    background: '#fff',
    borderRadius: '16px',
    padding: '60px 20px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  noSelectionIcon: { fontSize: '48px', marginBottom: '16px' },
  noSelectionText: { fontSize: '14px', color: '#6B7280' },
  detailCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '22px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    overflowY: 'auto',
    maxHeight: '600px',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '14px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  detailTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '18px',
    color: '#1C1C1E',
    marginBottom: '4px',
  },
  detailMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
  },
  deleteBtn: {
    background: '#FFEBEE',
    color: '#C62828',
    border: 'none',
    padding: '7px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  badgeRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  badge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#F3F4F6',
    color: '#6B7280',
    fontWeight: '500',
  },
  section: {
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #F3F4F6',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '10px',
    fontWeight: '600',
  },
  sectionText: {
    fontSize: '13.5px',
    color: '#374151',
    lineHeight: 1.6,
  },
  progressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    borderBottom: '1px solid #F9FAFB',
  },
  progressLabel: {
    fontSize: '13px',
    color: '#374151',
  },
  progressBadge: {
    fontSize: '11px',
    padding: '3px 10px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
    gap: '8px',
    marginTop: '8px',
  },
  photo: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: '8px',
    cursor: 'pointer',
    border: '1px solid #E5E7EB',
  },
  docItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    background: '#F9FAFB',
    borderRadius: '9px',
    border: '1px solid #E5E7EB',
    cursor: 'pointer',
    marginBottom: '8px',
  },
  docName: {
    flex: 1,
    fontSize: '13px',
    color: '#1C1C1E',
  },
  docOpen: {
    fontSize: '12px',
    color: '#14452F',
    fontWeight: '600',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '30px 0',
  },
};